import React from 'react';
import { Clock, Calendar, MapPin, Mail, Phone, Package, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function CancellationRequestCard({ 
  request, 
  onApprove, 
  onReject, 
  calculateRefundAmount,
  selectionMode = false,
  isSelected = false,
  onSelectionChange 
}) {
  const hoursUntilEvent = request?.event 
    ? (new Date(request.event.eventDate)?.getTime() - new Date()?.getTime()) / (1000 * 60 * 60)
    : 999;
  
  const isUrgent = hoursUntilEvent <= 48;
  const refundAmount = calculateRefundAmount(request);

  return (
    <div className={`bg-white rounded-lg shadow-sm border-2 transition-all hover:shadow-md ${
      isUrgent ? 'border-orange-400 bg-orange-50' : isSelected ?'border-blue-500 bg-blue-50' : 'border-gray-200'
    }`}>
      <div className="p-6">
        {/* Selection Checkbox & Urgent Badge Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {/* Selection Checkbox */}
            {selectionMode && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelectionChange?.(request?.id, e?.target?.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
              />
            )}
            
            {/* Urgent Badge */}
            {isUrgent && (
              <div className="flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                <AlertCircle className="w-4 h-4" />
                <span>Urgent - Event in {Math.floor(hoursUntilEvent)}h</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column - Event & Participant Info */}
          <div className="space-y-4">
            {/* Event Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {request?.event?.title || 'Event'}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>
                    {request?.event?.eventDate 
                      ? format(new Date(request.event.eventDate), 'MMMM dd, yyyy')
                      : 'Date not available'}
                  </span>
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{request?.event?.eventTime || 'Time not specified'}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{request?.event?.location || 'Location not specified'}</span>
                </div>
              </div>
            </div>

            {/* Participant Details */}
            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-2">Participant Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <span className="font-medium text-gray-700 mr-2">Name:</span>
                  <span className="text-gray-600">
                    {request?.participant?.fullName || request?.participantName || 'N/A'}
                  </span>
                </div>
                <div className="flex items-start">
                  <Mail className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-gray-400" />
                  <span className="text-gray-600">
                    {request?.participant?.email || request?.participantEmail || 'N/A'}
                  </span>
                </div>
                {(request?.participant?.phone || request?.participantPhone) && (
                  <div className="flex items-start">
                    <Phone className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 text-gray-400" />
                    <span className="text-gray-600">
                      {request?.participant?.phone || request?.participantPhone}
                    </span>
                  </div>
                )}
                {request?.equipmentRental && (
                  <div className="flex items-center text-blue-600">
                    <Package className="w-4 h-4 mr-2" />
                    <span className="font-medium">Equipment Rental Included</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Cancellation Details & Actions */}
          <div className="space-y-4">
            {/* Cancellation Request Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">Cancellation Request</h4>
                <span className="text-xs text-gray-500">
                  {request?.cancellationRequestedAt
                    ? format(new Date(request.cancellationRequestedAt), 'MMM dd, yyyy HH:mm')
                    : 'Date not available'}
                </span>
              </div>
              
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Reason:</p>
                <p className="text-sm text-gray-600 bg-white rounded p-3 border border-gray-200">
                  {request?.cancellationRequestReason || 'No reason provided'}
                </p>
              </div>

              {request?.specialRequirements && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Special Requirements:</p>
                  <p className="text-sm text-gray-600 bg-white rounded p-3 border border-gray-200">
                    {request?.specialRequirements}
                  </p>
                </div>
              )}
            </div>

            {/* Refund Calculation */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 mb-2">Estimated Refund</h4>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-green-700">₺{refundAmount}</span>
                <span className="text-sm text-green-600">
                  {hoursUntilEvent >= 72 ? '100%' : hoursUntilEvent >= 48 ? '75%' : hoursUntilEvent >= 24 ? '50%' : '0%'} refund
                </span>
              </div>
              <p className="text-xs text-green-600 mt-2">
                Based on {Math.floor(hoursUntilEvent)}h until event
              </p>
            </div>

            {/* Action Buttons - Hide in selection mode */}
            {!selectionMode && (
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => onApprove(request)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Approve</span>
                </button>
                <button
                  onClick={() => onReject(request)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  <XCircle className="w-5 h-5" />
                  <span>Reject</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}